import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

type RequestFn<T> = () => Promise<T>;

interface ScheduledTask<T> {
  run: () => void;
  signal?: AbortSignal;
  meta?: { label?: string; url?: string };
  retriesLeft: number;
}

class RequestLimiter {
  private queue: ScheduledTask<any>[] = [];
  private isPaused = false;
  private intervalMs: number;
  private lastRunTime = 0;
  private intervalId: NodeJS.Timeout | null = null;
  private isTicking = false;

  constructor(requestsPerSecond: number) {
    this.intervalMs = Math.floor(1000 / requestsPerSecond);
  }

  private startTicking() {
    if (this.isTicking) return;
    this.isTicking = true;

    this.intervalId = setInterval(() => {
      if (this.isPaused) return;

      if (this.queue.length === 0) {
        this.stopTicking();
        return;
      }

      const now = Date.now();
      if (now - this.lastRunTime < this.intervalMs) return;

      const task = this.queue.shift();
      if (!task) return;

      const { run, signal } = task;
      if (signal?.aborted) {
        console.log('[Limiter] 🗑️ Skipped aborted request in queue');
        return;
      }

      this.lastRunTime = now;
      run();
    }, 50);
  }

  private stopTicking() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.isTicking = false;
  }

  public schedule<T>(
    fn: RequestFn<T>,
    signal?: AbortSignal,
    meta?: { label?: string; url?: string },
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const abortHandler = () => {
        this.queue = this.queue.filter((task) => task.run !== run);
        reject(new DOMException('Aborted', 'AbortError'));
      };

      const run = () => {
        if (signal?.aborted) {
          reject(new DOMException('Aborted', 'AbortError'));
          return;
        }

        signal?.removeEventListener('abort', abortHandler);

        fn()
          .then(resolve)
          .catch((error) => {
            if (this.isRateLimitError(error)) {
              if (task.retriesLeft > 0) {
                const retryTask: ScheduledTask<any> = {
                  run,
                  signal,
                  meta,
                  retriesLeft: task.retriesLeft - 1,
                };

                setTimeout(() => {
                  if (this.queue.length > 1) {
                    this.queue.splice(1, 0, retryTask);
                  } else {
                    this.queue.unshift(retryTask);
                  }
                  this.startTicking();
                }, 1000);

                this.pause(500);
              } else {
                reject(error);
              }
              return;
            }

            reject(error);
          });
      };

      if (signal?.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }

      signal?.addEventListener('abort', abortHandler);

      const task: ScheduledTask<any> = { run, signal, meta, retriesLeft: 3 };
      this.queue.push(task);

      this.startTicking();
    });
  }

  private pause(ms: number) {
    this.isPaused = true;
    setTimeout(() => {
      this.isPaused = false;
    }, ms);
  }

  private isRateLimitError(error: any): boolean {
    return (
      error?.response?.status === 429 ||
      error?.status === 429 ||
      error?.code === 'ERR_TOO_MANY_REQUESTS'
    );
  }
}

const limiter = new RequestLimiter(2); // 2 запроса в секунду (1 каждые 500мс)
export default limiter;

export function limitedAxios<T = any>(config: AxiosRequestConfig): Promise<AxiosResponse<T>> {
  return limiter.schedule(() => axios(config));
}
